
/**
 * 属性なし（なし）カードのトリガー実装
 *
 * このファイルには属性なしカードの個別トリガー効果を実装します。
 * 各カードは独自のトリガー定義を持ち、triggerEngineによって管理されます。
 */

import { TRIGGER_TYPES, ACTIVATION_TYPES, TRIGGER_PRIORITIES } from '../triggerTypes';
import type { TriggerContext } from '../triggerEngine';
import {
  getPlayerContext,
  conditionalDamage,
  drawCards,
  healLife,
  modifyAttack,
  modifyHP,
} from '../effectHelpers';
import { hasCategory } from '../../utils/helpers';
import type { CardWithId, MonsterInstance } from '../../types/card';
import type { TriggerDefinition } from '../../types/effect';
import {
  unlockDeepSeaZone,
  submergeCard,
  incrementDepth,
  surfaceCard,
} from '../deepSeaZone';
import type { DeepSeaZone, DeepSeaCard } from '../deepSeaZone/types';

/**
 * 属性なしカードのトリガー定義
 * カードID => トリガー配列のマッピング
 */
export const neutralCardTriggers = {
  // ========================================
  // モンスターカード
  // ========================================

  /**
   * C0000189: 旅の助っ人
   * 【自分メインフェイズ時、墓地で発動】このカードを除外して、レスト状態のSPトークンを１つアクティブにする。
   */
  C0000189: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_FROM_GRAVEYARD,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: '墓地発動: 除外してSP1回復',
      priority: TRIGGER_PRIORITIES.NORMAL,
      effect: (context: TriggerContext) => {
        const { card, addLog } = context;
        const { myRestedSP, setMyGraveyard, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        if (myRestedSP < 1) {
          addLog('旅の助っ人: レスト状態のSPがありません', 'info');
          return false;
        }

        // 墓地から除外
        const cardUniqueId = card && 'uniqueId' in card ? card.uniqueId : undefined;
        if (!cardUniqueId) return false;
        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== cardUniqueId));

        // SPをアクティブに
        setMyActiveSP((prev) => prev + 1);
        setMyRestedSP((prev) => prev - 1);

        addLog('旅の助っ人を除外してSPを1回復！', 'heal');
        return true;
      },
    },
  ],

  /**
   * C0000190: 守りの歩哨
   * 【自壊時】自分のライフを300回復。
   */
  C0000190: [
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '自壊時: ライフ300回復',
      effect: (context: TriggerContext) => {
        healLife(context, 300, true);
        context.addLog('守りの歩哨の効果: ライフを300回復！', 'heal');
      },
    },
  ],

  /**
   * C0000191: 反撃の使者
   * 【相手モンスターに攻撃された時】、戦闘終了後にその相手モンスターに1200ダメージを与える。
   */
  C0000191: [
    {
      type: TRIGGER_TYPES.ON_ATTACKED,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '被攻撃時: 相手モンスターに1200ダメージ',
      effect: (context: TriggerContext) => {
        const { attackerIndex, addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        // 攻撃者は相手側のモンスター
        if (attackerIndex !== undefined && opponentField[attackerIndex]) {
          setOpponentField((prev) => {
            const newField = [...prev];
            if (newField[attackerIndex]) {
              const newHp = newField[attackerIndex].currentHp - 1200;
              if (newHp <= 0) {
                addLog(`${newField[attackerIndex].name}が反撃で破壊された！`, 'damage');
                newField[attackerIndex] = null;
              } else {
                newField[attackerIndex] = { ...newField[attackerIndex], currentHp: newHp };
                addLog(`反撃の使者の効果: ${newField[attackerIndex].name}に1200ダメージ！`, 'damage');
              }
            }
            return newField;
          });
        }
      },
    },
  ],

  /**
   * C0000192: 影の運び手
   * 【召喚時】自分の墓地のモンスター1体をデッキに戻し、自分のレスト状態のSPトークン１つをアクティブにする。
   */
  C0000192: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 墓地モンスター→デッキ、SP1回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myGraveyard, setMyGraveyard, setMyDeck, myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        const monsters = myGraveyard.filter((c) => c.type === 'monster');

        if (monsters.length === 0) {
          addLog('影の運び手: 墓地にモンスターがいません', 'info');
          return false;
        }

        // 最初のモンスターをデッキに戻す
        const targetMonster = monsters[0];

        setMyGraveyard((prev) => prev.filter((c) => c.uniqueId !== targetMonster.uniqueId));
        setMyDeck((prev) => {
          const newDeck = [...prev, targetMonster];
          // シャッフル
          for (let i = newDeck.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [newDeck[i], newDeck[j]] = [newDeck[j], newDeck[i]];
          }
          return newDeck;
        });

        addLog(`影の運び手の効果: ${targetMonster.name}をデッキに戻した！`, 'info');

        // SPをアクティブに
        if (myRestedSP >= 1) {
          setMyActiveSP((prev) => prev + 1);
          setMyRestedSP((prev) => prev - 1);
          addLog('影の運び手の効果: SPを1回復！', 'heal');
        }

        return true;
      },
    },
  ],

  /**
   * C0000205: 囁く賢者
   * 【召喚時】自分のデッキの上から3枚を確認し、好きな順番で戻す。
   * 【墓地に送られたとき】デッキから1枚ドローする。
   */
  C0000205: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: デッキトップ3枚を並び替え',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview } = context;
        const { myDeck, setMyDeck } = getPlayerContext(context);

        if (myDeck.length === 0) {
          addLog('囁く賢者の効果: デッキにカードがありません', 'info');
          return;
        }

        // デッキ上3枚を確認
        const cardCount = Math.min(3, myDeck.length);
        const topCards = myDeck.slice(0, cardCount);
        addLog(`囁く賢者の効果: デッキ上${cardCount}枚を確認`, 'info');

        // デッキ確認モーダルを表示
        if (setPendingDeckReview) {
          setPendingDeckReview({
            cards: topCards,
            title: '囁く賢者',
            message: 'ドラッグして順番を変更し、確定してください',
            allowReorder: true,
            onConfirm: (reorderedCards: CardWithId[]) => {
              // 並び替えた順番でデッキを更新
              setMyDeck((prev) => [...reorderedCards, ...prev.slice(cardCount)]);
              addLog(`カードをデッキの上に戻した: ${reorderedCards.map((c: CardWithId) => c.name).join(' → ')}`, 'info');
            },
            selectionOwner: context.currentPlayer,
          } as any);
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_DESTROY_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '墓地送り時: 1枚ドロー',
      effect: (context: TriggerContext) => {
        drawCards(context, 1);
        context.addLog('囁く賢者の効果: 1枚ドロー！', 'info');
      },
    },
  ],

  /**
   * C0000219: 団長プニリーヌ・ソフティア
   * 【常時】場にいる間、［蛮族］モンスターの攻撃力300アップ。
   * 【エンドフェイズ時】HPを500回復。
   */
  C0000219: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 蛮族の攻撃力+300',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: HP500回復',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyField } = getPlayerContext(context);

        // このカードのHP回復
        setMyField((prev) => {
          return prev.map((monster) => {
            if (monster && monster.name === '団長プニリーヌ・ソフティア') {
              const currentHp = monster.currentHp ?? monster.hp ?? 0;
              const maxHp = monster.hp ?? 0;
              const newHp = Math.min(currentHp + 500, maxHp);
              addLog(`団長プニリーヌ・ソフティアのHPが${newHp - currentHp}回復！`, 'heal');
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });
      },
    },
  ],

  /**
   * C0000220: 鉄槍騎士団のゲスール
   * 【エンドフェイズ時】《プニリーヌ》モンスターが場にいる場合、相手モンスター全体に500ダメージ。
   */
  C0000220: [
    {
      type: TRIGGER_TYPES.ON_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: プニリーヌがいれば全体500ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setOpponentField } = getPlayerContext(context);

        // プニリーヌがいるかチェック
        const hasPunirine = myField.some((m) => m && m.name && m.name.includes('プニリーヌ'));

        if (!hasPunirine) {
          return;
        }

        let damaged = false;
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              damaged = true;
              const newHp = monster.currentHp - 500;
              if (newHp <= 0) {
                addLog(`${monster.name}がゲスールの効果で破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        if (damaged) {
          addLog('ゲスールの効果: 相手全体に500ダメージ！', 'damage');
        }
      },
    },
  ],

  /**
   * C0000221: 鉄槍騎士団のマグラ
   * 【召喚時】相手の手札1枚をランダムに墓地に送る。
   */
  C0000221: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 相手手札1枚を墓地へ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { opponentHand, setOpponentHand, setOpponentGraveyard } = getPlayerContext(context);

        if (opponentHand.length === 0) {
          addLog('マグラの効果: 相手の手札がありません', 'info');
          return;
        }

        const randomIndex = Math.floor(Math.random() * opponentHand.length);
        const discardedCard = opponentHand[randomIndex];

        setOpponentHand((prev) => prev.filter((_, i) => i !== randomIndex));
        setOpponentGraveyard((prev) => [...prev, discardedCard]);

        addLog(`マグラの効果: 相手の${discardedCard.name}を墓地に送った！`, 'damage');
      },
    },
  ],

  /**
   * C0000222: 鉄槍騎士団のウィア
   * 【常時】《プニリーヌ》モンスターが場にいるときこのカードの攻撃力を500アップする。
   */
  C0000222: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: プニリーヌがいれば攻撃力+500',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
  ],

  /**
   * C0000224: 鉄槍騎士団の奴隷プニリーヌ
   * 【召喚時】場にいる［蛮族］モンスターの数×600ダメージを相手モンスター全体と相手プレイヤーに与える。
   * 【エンドフェイズ時】自分のライフを300減らす。
   */
  C0000224: [
    {
      type: TRIGGER_TYPES.ON_SUMMON,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '召喚時: 蛮族数×600ダメージ（全体＋プレイヤー）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setOpponentField } = getPlayerContext(context);

        // 蛮族の数をカウント
        const barbarianCount = myField.filter((m) => m && hasCategory(m, '【蛮族】')).length;

        if (barbarianCount === 0) {
          addLog('奴隷プニリーヌの効果: 蛮族がいません', 'info');
          return;
        }

        const damage = barbarianCount * 600;

        // 相手モンスター全体にダメージ
        setOpponentField((prev) => {
          return prev.map((monster) => {
            if (monster) {
              const newHp = monster.currentHp - damage;
              if (newHp <= 0) {
                addLog(`${monster.name}が奴隷プニリーヌの効果で破壊された！`, 'damage');
                return null;
              }
              return { ...monster, currentHp: newHp };
            }
            return monster;
          });
        });

        // 相手プレイヤーにダメージ
        conditionalDamage(context as any, damage, 'opponent');
        addLog(`奴隷プニリーヌの効果: ${barbarianCount}体×600=${damage}ダメージ！`, 'damage');
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: ライフ-300',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { setMyLife } = getPlayerContext(context);
        setMyLife((prev) => prev - 300);
        addLog('奴隷プニリーヌの代償: ライフ-300', 'damage');
      },
    },
  ],

  /**
   * C0000310: 鉄槍の訓練兵
   * 【変幻身】メインフェイズにSPコストを2払うことで場のこのモンスターを別の形態に変化する。
   */
  C0000310: [
    {
      type: TRIGGER_TYPES.ON_MAIN_PHASE_SELF,
      activationType: ACTIVATION_TYPES.OPTIONAL,
      description: 'メインフェイズ時: 変幻身（SP2払い形態変化）',
      costCheck: (context: TriggerContext) => {
        const { myActiveSP } = getPlayerContext(context);
        return myActiveSP >= 2;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        // TODO: 変幻身システムの実装が必要
        addLog('鉄槍の訓練兵: 変幻身（未実装）', 'info');
      },
    },
  ],

  /**
   * C0000317: 鉄槍の騎士長
   * 【常時】自分の《鉄槍》モンスターの攻撃力を300アップ。
   */
  C0000317: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 鉄槍モンスターの攻撃力+300',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
  ],

  /**
   * C0000401: 呪術狩りの傭兵バランド
   * 【常時】自分の他のモンスターが闇属性モンスターに与えるダメージを200アップ。
   */
  C0000401: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 闇属性へのダメージ+200',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
  ],

  // ========================================
  // フィールドカード
  // ========================================

  /**
   * C0000533: 荒海の海溝・アビストレンチ
   * 【常時】「深海領域」を開放する。深海領域の上限を3→5に拡張。
   * 【自分ターン終了時】深海領域のカード全ての深度+1（通常の+1と別に）。
   */
  C0000533: [
    {
      type: TRIGGER_TYPES.ON_FIELD_CARD_SET,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '設置時: 深海領域を開放',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

        if (!setMyDeepSeaZone) {
          addLog('荒海の海溝・アビストレンチ: 深海領域システムが利用できません', 'info');
          return;
        }

        // 深海領域を開放（既に開放済みならスキップ）
        if (!myDeepSeaZone?.isUnlocked) {
          setMyDeepSeaZone((prev) => unlockDeepSeaZone(prev));
          addLog('荒海の海溝・アビストレンチ: 深海領域を開放！', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'ターン終了時: 深海領域の全カード深度+1（追加）',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;

        if (!setMyDeepSeaZone || !myDeepSeaZone?.isUnlocked) {
          return;
        }

        if (myDeepSeaZone.cards.length === 0) {
          return;
        }

        // 全カードの深度を+1（追加ボーナス）
        setMyDeepSeaZone((prev) => {
          const updatedCards = prev.cards.map((card) => ({
            ...card,
            depth: card.depth + 1,
          }));
          return { ...prev, cards: updatedCards };
        });

        addLog(`荒海の海溝・アビストレンチ: 深海領域の全カードの深度+1！`, 'info');
      },
    },
  ],

  /**
   * C0000229: 鉄槍騎士団の宿舎
   * 【常時】《鉄槍》モンスターの攻撃力を400アップ。
   * 【エンドフェイズ時】《プニリーヌ》モンスターが場にいればレスト状態のSPを１つアクティブにする。
   * 場に【蛮族】モンスターが3体以上いるとき相手モンスター全体に200ダメージを与え、攻撃力を200ダウン。
   */
  C0000229: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 鉄槍モンスターの攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: プニリーヌがいればSP回復＆蛮族3体以上で全体ダメージ',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, setOpponentField, myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        // プニリーヌチェック
        const hasPunirine = myField.some((m) => m && m.name && m.name.includes('プニリーヌ'));
        if (hasPunirine) {
          if (myRestedSP >= 1) {
            setMyActiveSP((prev) => prev + 1);
            setMyRestedSP((prev) => prev - 1);
            addLog('鉄槍騎士団の宿舎: SPを1回復！', 'heal');
          }
        }

        // 蛮族3体以上チェック
        const barbarianCount = myField.filter((m) => m && hasCategory(m, '【蛮族】')).length;
        if (barbarianCount >= 3) {
          setOpponentField((prev) => {
            return prev.map((monster) => {
              if (monster) {
                const newHp = monster.currentHp - 200;
                const newAttack = monster.currentAttack - 200;
                if (newHp <= 0) {
                  addLog(`${monster.name}が宿舎の効果で破壊された！`, 'damage');
                  return null;
                }
                return { ...monster, currentHp: newHp, currentAttack: Math.max(0, newAttack) };
              }
              return monster;
            });
          });
          addLog('鉄槍騎士団の宿舎: 相手全体に200ダメージ＆攻撃力-200！', 'damage');
        }
      },
    },
  ],

  /**
   * C0000140: 鎖の迷宮
   * 【相手モンスター攻撃時】その攻撃力を次のターンのエンドフェイズ時まで500ダウン。
   */
  C0000140: [
    {
      type: TRIGGER_TYPES.ON_OPPONENT_ATTACK,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '相手攻撃時: 攻撃力-500（次ターン終了まで）',
      effect: (context: TriggerContext) => {
        const { attacker, attackerIndex, addLog } = context;
        const { opponentField, setOpponentField } = getPlayerContext(context);

        if (!attacker || attackerIndex === undefined) {
          return;
        }

        setOpponentField(prev => {
          const newField = [...prev];
          const monster = newField[attackerIndex];
          if (monster) {
            const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
            newField[attackerIndex] = {
              ...monster,
              currentAttack: Math.max(0, currentAtk - 500),
              // 次ターンのエンドフェイズまで持続するフラグ
              chainDebuff: {
                value: 500,
                expiresAtEndOfNextTurn: true,
              },
            } as any;
          }
          return newField;
        });

        addLog(`鎖の迷宮の効果: ${attacker.name}の攻撃力-500！（次ターン終了まで）`, 'damage');
      },
    },
  ],

  /**
   * C0000242: ご主人様のアパート
   * 【常時】《ご主人様》または《リリカ》と名の付くモンスターの攻撃力を400アップ。
   * 【自分エンドフェイズ時】《ご主人様》が場にいるとき、1枚ドローする。
   */
  C0000242: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: ご主人様/リリカの攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: ご主人様がいれば1ドロー',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        const hasGoshujinsama = myField.some((m) => m && m.name && m.name.includes('ご主人様'));

        if (hasGoshujinsama) {
          drawCards(context, 1);
          addLog('ご主人様のアパートの効果: 1枚ドロー！', 'info');
        }
      },
    },
  ],

  /**
   * C0000289: 薬師の隠れ家
   * 【常時】場にいる《毒》と名の付くモンスターの攻撃力を400アップ。
   * 【自分エンドフェイズ時】自分のライフを300回復。
   */
  C0000289: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 毒モンスターの攻撃力+400',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: ライフ300回復',
      effect: (context: TriggerContext) => {
        healLife(context, 300, true);
        context.addLog('薬師の隠れ家の効果: ライフ300回復！', 'heal');
      },
    },
  ],

  /**
   * C0000376: 虹羽の微脈
   * 【常時】自分のターン中《虹羽密林》モンスターの攻撃力を500アップ。
   * 【自分エンドフェイズ時】自分の場の《虹羽密林》モンスターの属性によって、以下の効果から1つを選択：
   *   水: ライフ300回復
   *   光: SPを1アクティブ
   *   炎: 相手モンスター1体に400ダメージ
   */
  C0000376: [
    {
      type: TRIGGER_TYPES.CONTINUOUS,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '常時: 虹羽密林モンスターの攻撃力+500（自分ターン中）',
      effect: (context: TriggerContext) => {
        // 常時効果は別システムで処理予定
      },
    },
    {
      type: TRIGGER_TYPES.ON_END_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: 'エンドフェイズ時: 虹羽密林の属性に応じた効果',
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField, opponentField, setOpponentField, myRestedSP, setMyActiveSP, setMyRestedSP } = getPlayerContext(context);

        const rainbowMonsters = myField.filter((m) => m && m.name && m.name.includes('虹羽密林'));

        if (rainbowMonsters.length === 0) {
          return;
        }

        // 最初に見つかった属性で効果を適用
        const firstMonster = rainbowMonsters[0];
        if (!firstMonster) return;
        const attribute = firstMonster.attribute;

        if (attribute === '水') {
          healLife(context, 300, true);
          addLog('虹羽の微脈（水）: ライフ300回復！', 'heal');
        } else if (attribute === '光') {
          if (myRestedSP >= 1) {
            setMyActiveSP((prev) => prev + 1);
            setMyRestedSP((prev) => prev - 1);
            addLog('虹羽の微脈（光）: SP1回復！', 'heal');
          }
        } else if (attribute === '炎') {
          const targets = opponentField.filter((m) => m !== null);
          if (targets.length > 0) {
            const randomIndex = opponentField.findIndex((m) => m !== null);
            setOpponentField((prev) => {
              const newField = [...prev];
              if (newField[randomIndex]) {
                const newHp = newField[randomIndex].currentHp - 400;
                if (newHp <= 0) {
                  addLog(`${newField[randomIndex].name}が虹羽の微脈で破壊された！`, 'damage');
                  newField[randomIndex] = null;
                } else {
                  newField[randomIndex] = { ...newField[randomIndex], currentHp: newHp };
                }
              }
              return newField;
            });
            addLog('虹羽の微脈（炎）: 相手モンスターに400ダメージ！', 'damage');
          }
        }
      },
    },
  ],

  // ========================================
  // フェーズカード
  // ========================================

  /**
   * C0000383: 虹羽密林の共鳴弦 (Phase Card)
   * 初期効果: 【自分のコスト3以下の水、光、炎属性モンスターの召喚時】、そのモンスターの攻撃力をターン終了時まで300アップ。
   * 1枚重ね: 【自分ドローフェイズ】虹羽密林モンスターがいればドロー+1
   * 2枚重ね: 【自分のコスト3以下の召喚時】SP1アクティブ（1ターンに1度）
   * 3枚重ね: 水/光/炎モンスター攻撃力+500、このカードを墓地へ
   */
  C0000383: [
    {
      type: TRIGGER_TYPES.ON_COST_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '初期効果: コスト3以下の水/光/炎モンスター召喚時、攻撃力+300',
      condition: (context: TriggerContext) => {
        const { card } = context;
        // 召喚されたカードがコスト3以下かつ水/光/炎属性
        return (
          card &&
          card.cost <= 3 &&
          ['水', '光', '炎'].includes(card.attribute)
        );
      },
      effect: (context: TriggerContext) => {
        const { card, slotIndex, addLog } = context;
        const { setMyField } = getPlayerContext(context);

        setMyField((prev) => {
          return prev.map((monster, idx) => {
            if (idx === slotIndex && monster) {
              const currentAtk = monster.currentAttack ?? monster.attack ?? 0;
              const existingBuff = (monster as any).attackBuffUntilEndOfTurn ?? 0;
              return {
                ...monster,
                currentAttack: currentAtk + 300,
                attackBuffUntilEndOfTurn: existingBuff + 300,
              } as any;
            }
            return monster;
          });
        });

        const cardName = card?.name ?? 'カード';
        addLog(`虹羽密林の共鳴弦【初期効果】: ${cardName}の攻撃力+300！`, 'info');
      },
    },
    {
      type: TRIGGER_TYPES.ON_DRAW_PHASE_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: 虹羽密林モンスターがいればドロー+1',
      condition: (context: TriggerContext) => {
        const { myPhaseCard } = getPlayerContext(context);
        return myPhaseCard && myPhaseCard.id === 'C0000383' && (myPhaseCard.stage || 0) >= 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog } = context;
        const { myField } = getPlayerContext(context);

        const hasRainbow = myField.some((m) => m && m.name && m.name.includes('虹羽密林'));

        if (hasRainbow) {
          drawCards(context, 1);
          addLog('虹羽密林の共鳴弦【1枚重ね】: 虹羽密林モンスターがいるため追加ドロー！', 'info');
        }
      },
    },
    {
      type: TRIGGER_TYPES.ON_COST_SUMMON_SELF,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: コスト3以下召喚時、SP1アクティブ',
      usedThisTurn: false,
      condition: (context: TriggerContext) => {
        const { card, trigger } = context;
        const { myPhaseCard } = getPlayerContext(context);
        // 召喚されたカードがコスト3以下かつ水/光/炎属性
        // かつフェイズカードが2枚重ね以上
        return (
          card &&
          card.cost <= 3 &&
          ['水', '光', '炎'].includes(card.attribute) &&
          myPhaseCard && myPhaseCard.id === 'C0000383' && (myPhaseCard.stage || 0) >= 2 &&
          !trigger?.usedThisTurn
        );
      },
      effect: (context: TriggerContext) => {
        const { addLog, trigger } = context;
        const { myRestedSP, setMyRestedSP, setMyActiveSP } = getPlayerContext(context);

        if (myRestedSP > 0) {
          setMyRestedSP((prev) => prev - 1);
          setMyActiveSP((prev) => prev + 1);
          addLog('虹羽密林の共鳴弦【2枚重ね】: SPトークン1個をアクティブに！', 'info');

          // 1ターンに1度のフラグを設定
          if (trigger) {
            trigger.usedThisTurn = true;
          }
        }
      },
    },
  ],

  /**
   * C0000521: 荒海の玉座・アビスロード (Phase Card)
   * 初期効果: 「深海領域」を開放する。手札から水属性モンスター1枚を深海領域に送れる。
   * 1枚重ね: デッキから1枚を深海領域に送る。または、深海領域の全カードに深度+1。
   * 2枚重ね: 深海領域のカード1枚を浮上させる。この浮上はコスト0で行える。
   * 3枚重ね: 深海領域の全カードを浮上させる（コスト不要）。浮上した全カードはこのターン攻撃可能。このカードは消滅する。
   */
  C0000521: [
    // 初期効果（ON_PHASE_CARD_SET）
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_SET,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '初期効果: 深海領域を開放し、水属性モンスター1枚を送れる',
      effect: (context: TriggerContext) => {
        const { addLog, setPendingHandSelection, turn } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;
        const { myHand, setMyHand } = getPlayerContext(context);

        if (!setMyDeepSeaZone) {
          addLog('荒海の玉座・アビスロード: 深海領域システムが利用できません', 'info');
          return;
        }

        // 深海領域を開放（既に開放済みならスキップ）
        if (!myDeepSeaZone?.isUnlocked) {
          setMyDeepSeaZone((prev) => unlockDeepSeaZone(prev));
          addLog('荒海の玉座・アビスロード: 深海領域を開放！', 'info');
        }

        // 深海領域の容量チェック（開放直後は0枚なのでスキップ可能）
        const currentCardCount = myDeepSeaZone?.cards?.length || 0;
        const capacity = myDeepSeaZone?.maxCapacity || 3;
        if (currentCardCount >= capacity) {
          addLog('荒海の玉座・アビスロード: 深海領域が満杯です', 'info');
          return;
        }

        // 手札に水属性モンスターがあるかチェック
        const waterMonsters = myHand.filter((c) => c.type === 'monster' && c.attribute === '水');
        if (waterMonsters.length === 0) {
          addLog('荒海の玉座・アビスロード: 手札に水属性モンスターがいません', 'info');
          return;
        }

        // 手札選択UI（OPTIONAL効果として選択可能）
        if (setPendingHandSelection) {
          setPendingHandSelection({
            message: '深海領域に送る水属性モンスターを選択（キャンセル可）',
            count: 1,
            filter: (card: any) => card.type === 'monster' && card.attribute === '水',
            allowCancel: true,
            callback: (selectedCards: any[]) => {
              if (selectedCards.length === 0) {
                addLog('荒海の玉座・アビスロード: モンスターを送らなかった', 'info');
                return;
              }

              const selectedCard = selectedCards[0];
              // 手札から削除
              setMyHand((prev) => prev.filter((c) => c.uniqueId !== selectedCard.uniqueId));
              // 深海領域に追加（開放済みとして追加）
              const currentTurn = (turn as number) || 1;
              setMyDeepSeaZone((prevZone) => {
                // 開放されていなければ開放してから追加
                const unlockedZone = prevZone.isUnlocked ? prevZone : unlockDeepSeaZone(prevZone);
                const result = submergeCard(unlockedZone, selectedCard, currentTurn);
                return result.updatedZone;
              });
              addLog(`荒海の玉座・アビスロード: ${selectedCard.name}を深海領域に送った！`, 'info');
            },
          });
        }
      },
    },

    // 1枚重ね効果（ON_PHASE_CARD_ACTIVATE）
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '1枚重ね: デッキから1枚を深海領域に送る or 全カード深度+1',
      condition: (context: TriggerContext) => {
        const stage = (context as any).stage || 0;
        return stage === 1;
      },
      effect: (context: TriggerContext) => {
        const { addLog, setPendingDeckReview, turn } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;
        const { myDeck, setMyDeck } = getPlayerContext(context);

        if (!setMyDeepSeaZone || !myDeepSeaZone?.isUnlocked) {
          addLog('アビスロード【1枚重ね】: 深海領域が利用できません', 'info');
          return;
        }

        // デッキに水属性モンスターがいるか確認
        const waterMonstersInDeck = myDeck.filter((c) => c.type === 'monster' && c.attribute === '水');
        const hasCardsInDeepSea = myDeepSeaZone.cards.length > 0;

        if (waterMonstersInDeck.length === 0 && !hasCardsInDeepSea) {
          addLog('アビスロード【1枚重ね】: デッキに水属性モンスターがなく、深海領域も空です', 'info');
          return;
        }

        // 選択肢を提示（デッキから送るか、深度+1か）
        // 簡略化のため、デッキに水属性がいれば優先的にデッキから送る
        if (waterMonstersInDeck.length > 0 && myDeepSeaZone.cards.length < myDeepSeaZone.maxCapacity) {
          // デッキから水属性モンスターを選択
          if (setPendingDeckReview) {
            setPendingDeckReview({
              cards: waterMonstersInDeck,
              count: 1,
              message: 'デッキから深海領域に送る水属性モンスターを選択（キャンセルで深度+1）',
              allowCancel: true,
              callback: (selectedCards: any[]) => {
                if (selectedCards.length === 0) {
                  // キャンセル = 深度+1を選択
                  if (hasCardsInDeepSea) {
                    setMyDeepSeaZone((prev) => incrementDepth(prev, 0).updatedZone);
                    addLog(`アビスロード【1枚重ね】: 深海領域の全カードに深度+1！`, 'info');
                  } else {
                    addLog('アビスロード【1枚重ね】: 深海領域にカードがありません', 'info');
                  }
                  return;
                }

                const selectedCard = selectedCards[0];
                // デッキから削除
                setMyDeck((prev) => prev.filter((c) => c.id !== selectedCard.id || c !== selectedCard));
                // 深海領域に追加
                const currentTurn = (turn as number) || 1;
                setMyDeepSeaZone((prevZone) => {
                  const result = submergeCard(prevZone, selectedCard, currentTurn);
                  return result.updatedZone;
                });
                addLog(`アビスロード【1枚重ね】: デッキから${selectedCard.name}を深海領域に送った！`, 'info');
              },
            });
          }
        } else if (hasCardsInDeepSea) {
          // デッキに水属性がないか深海領域が満杯なら深度+1
          setMyDeepSeaZone((prev) => incrementDepth(prev, 0).updatedZone);
          addLog(`アビスロード【1枚重ね】: 深海領域の全カードに深度+1！`, 'info');
        }
      },
    },

    // 2枚重ね効果（ON_PHASE_CARD_ACTIVATE）
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '2枚重ね: 深海領域のカード1枚をコスト0で浮上',
      condition: (context: TriggerContext) => {
        const stage = (context as any).stage || 0;
        return stage === 2;
      },
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer, setPendingMonsterTarget } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;
        const { myField, setMyField } = getPlayerContext(context);

        if (!setMyDeepSeaZone || !myDeepSeaZone?.isUnlocked || myDeepSeaZone.cards.length === 0) {
          addLog('アビスロード【2枚重ね】: 深海領域にカードがありません', 'info');
          return;
        }

        // 空きスロットを確認
        const emptySlots = myField.map((m, i) => m === null ? i : -1).filter(i => i >= 0);
        if (emptySlots.length === 0) {
          addLog('アビスロード【2枚重ね】: フィールドに空きがありません', 'info');
          return;
        }

        // 浮上させるカードを選択（簡略化: 最初のカードを自動浮上）
        const deepSeaCard = myDeepSeaZone.cards[0];
        const result = surfaceCard(myDeepSeaZone, 0, deepSeaCard.card.cost); // コスト分軽減 = コスト0

        if (result.success && result.surfacedCard) {
          const targetSlot = emptySlots[0];
          const surfacedMonster = {
            ...result.surfacedCard,
            currentHp: result.surfacedCard.hp || 1000,
            maxHp: result.surfacedCard.hp || 1000,
            attack: (result.surfacedCard.attack || 0) + result.bonus.attackBonus,
            currentAttack: (result.surfacedCard.attack || 0) + result.bonus.attackBonus,
            canAttack: result.bonus.canPreemptiveStrike,
            charges: [],
            owner: currentPlayer,
            statusEffects: [],
          } as unknown as MonsterInstance;

          setMyField((prev) => {
            const newField = [...prev];
            newField[targetSlot] = surfacedMonster;
            return newField;
          });
          setMyDeepSeaZone(() => result.updatedZone);

          addLog(`アビスロード【2枚重ね】: ${result.surfacedCard.name}をコスト0で浮上！（深度${deepSeaCard.depth}）`, 'info');
        }
      },
    },

    // 3枚重ね効果（ON_PHASE_CARD_ACTIVATE）
    {
      type: TRIGGER_TYPES.ON_PHASE_CARD_ACTIVATE,
      activationType: ACTIVATION_TYPES.AUTOMATIC,
      description: '3枚重ね: 全カードをコスト0で浮上（攻撃可能）',
      condition: (context: TriggerContext) => {
        const stage = (context as any).stage || 0;
        return stage === 3;
      },
      effect: (context: TriggerContext) => {
        const { addLog, currentPlayer } = context;
        const myDeepSeaZone = (context as any).myDeepSeaZone as DeepSeaZone | undefined;
        const setMyDeepSeaZone = (context as any).setMyDeepSeaZone as ((updater: (prev: DeepSeaZone) => DeepSeaZone) => void) | undefined;
        const { myField, setMyField } = getPlayerContext(context);

        if (!setMyDeepSeaZone || !myDeepSeaZone?.isUnlocked || myDeepSeaZone.cards.length === 0) {
          addLog('アビスロード【3枚重ね】: 深海領域にカードがありません', 'info');
          return;
        }

        // 空きスロットを確認
        const emptySlots = myField.map((m, i) => m === null ? i : -1).filter(i => i >= 0);

        let surfacedCount = 0;
        let currentZone = { ...myDeepSeaZone };
        const newField = [...myField];

        // 全カードを浮上（空きスロットがある限り）
        while (currentZone.cards.length > 0 && emptySlots.length > surfacedCount) {
          const deepSeaCard = currentZone.cards[0];
          const result = surfaceCard(currentZone, 0, deepSeaCard.card.cost); // コスト0

          if (result.success && result.surfacedCard) {
            const targetSlot = emptySlots[surfacedCount];
            const surfacedMonster = {
              ...result.surfacedCard,
              currentHp: result.surfacedCard.hp || 1000,
              maxHp: result.surfacedCard.hp || 1000,
              attack: (result.surfacedCard.attack || 0) + result.bonus.attackBonus,
              currentAttack: (result.surfacedCard.attack || 0) + result.bonus.attackBonus,
              canAttack: true, // 3枚重ね効果: このターン攻撃可能
              charges: [],
              owner: currentPlayer,
              statusEffects: [],
            } as unknown as MonsterInstance;

            newField[targetSlot] = surfacedMonster;
            currentZone = result.updatedZone;
            surfacedCount++;

            addLog(`アビスロード【3枚重ね】: ${result.surfacedCard.name}が浮上！（攻撃可能）`, 'info');
          } else {
            break;
          }
        }

        if (surfacedCount > 0) {
          setMyField(() => newField);
          setMyDeepSeaZone(() => currentZone);
          addLog(`アビスロード【3枚重ね】: ${surfacedCount}体のモンスターが一斉浮上！`, 'info');
        }
      },
    },
  ],
};

// ========================================
// ヘルパー関数
// ========================================

/**
 * カードIDに対応するトリガーが定義されているかチェック
 * @param {string} cardId - カードID
 * @returns {boolean} トリガーが定義されている場合true
 */
export const hasNeutralCardTrigger = (cardId: string): boolean => {
  return Boolean(cardId && (neutralCardTriggers as Record<string, unknown>)[cardId] !== undefined);
};

/**
 * カードIDに対応するトリガー配列を取得
 * @param {string} cardId - カードID
 * @returns {Array|null} トリガー配列、または未定義の場合null
 */
export const getNeutralCardTriggers = (cardId: string): TriggerDefinition[] | null => {
  const triggers = neutralCardTriggers as unknown as Record<string, TriggerDefinition[]>;
  if (!cardId || !triggers[cardId]) {
    return null;
  }
  return triggers[cardId];
};

/**
 * 実装されているカード数を取得
 * @returns {number} 実装カード数
 */
export const getNeutralCardTriggersCount = () => {
  return Object.keys(neutralCardTriggers).length;
};
